import { defineXray } from "../src/xray/manifest.js";
import { SHELL_TOOL_NAMES } from "../src/mine/shell.js";
import { extractWaitOps, describeMissing } from "../extractors/wait/index.js";
import { collect, build, groupBy, anchor, ignore } from "../extractors/wait/spec.js";

const PROBE_URL_RE = /https?:\/\/[^\s"'`;)|&\\]+/;
// A path with at least two segments. One segment is a bare word behind a slash,
// which is no more of an identity than the word alone.
const PROBE_PATH_RE = /(?:^|[\s"'=])((?:\.{0,2})\/[\w.-]+(?:\/[\w.-]+)+)/;
// The universal sink and its siblings. `docker ps >/dev/null` names one on every
// machine on earth, so reading it as the thing being waited for would merge every
// unrelated wait in a window into one target.
const NOT_A_TARGET_RE = /^\/dev\//;

/**
 * What a probe was pointed AT, when it names something. A `docker ps` names
 * nothing, and inventing an identity from the program would make every container
 * wait in a window look like the same wait.
 */
function probeTarget(probe) {
  const s = String(probe || "");
  const url = PROBE_URL_RE.exec(s);
  if (url) return url[0];
  const path = PROBE_PATH_RE.exec(s)?.[1];
  return path && !NOT_A_TARGET_RE.test(path) ? path : null;
}

export default defineXray({
  id: "wait",
  label: "Waits",
  title: "Where a session sat blocked on something it did not control — what it was waiting for, "
    + "the deadline it set itself, and whether it saw the thing become ready or gave up and carried on",

  consumer: "someone auditing a long agent run that reported success",
  decision: "whether a step that depended on CI, a service, or a build actually observed it ready — or timed out silently and proceeded on an assumption",

  // TRACE-ONLY. A wait's subject is the agent's own control flow, not a target in
  // the world, so it does not aggregate: read across sessions it becomes
  // "sessions poll logs", which is a mechanism tally rather than a pattern anyone
  // acts on. Read inline, next to what the agent did next, "it gave up on the
  // container after 120s and carried on" is a finding.
  tab: false,
  tabReason: "process-level: the op's subject is the session's own execution, so it "
    + "only means something beside the step that depended on it",

  // Trace-only, so this does not down-select a tab of its own — it is how the
  // LINEAGE window finds the prompts carrying this lens's work. Same matcher
  // its tab-bearing siblings use: every op here is read off a shell command.
  substrate: { label: "shell (blocking waits)", match: (n) => SHELL_TOOL_NAMES.test(String(n).split("__").pop()) },

  feed: { domain: "wait" },
  extract: extractWaitOps,
  collect,
  group: build,
  groupBy,
  anchor,
  ignore,

  // A wait is never a production — it is the session declining to proceed — but it
  // is a strong SOURCE: the thing it blocked on is usually the thing the next step
  // depended on, and when the probe names a URL or a path that identity is shared
  // with whatever else in the session touched it.
  lineage: {
    kind: "wait",
    ref: (op) => probeTarget(op.probe),
    role: () => "consultation",
    text: (op) => op.output || "",
    label: (op) => `waited on ${op.phrase}`,
    intent: (op) => (op.budgetSecs ? `${op.budgetSecs}s budget · ${op.outcome}` : op.outcome || null),
  },

  emptyNote: "No blocking waits in this window",
  explainMissing: describeMissing,
});
